import { Button, Link } from "@heroui/react";
import { useState } from "react";

import { AppLogo } from "@/src/components/AppLogo";
import { ChevronLeftIcon } from "@/src/components/icons";
import { AccountCard } from "@/src/components/layout/AccountCard";
import { SidebarNav } from "@/src/components/layout/SidebarNav";
import { useTranslations } from "@/src/i18n/i18n";
import { readStorage, writeStorage } from "@/src/libs/storage";
import { ROUTES } from "@/src/routes/paths";

const STORAGE_KEY = "sidebar-collapsed";

/**
 * Barre latérale repliée ou non, mémorisée.
 *
 * Pas d'`useSyncExternalStore` ici, contrairement au thème et à la langue :
 * l'état n'est lu que par ce composant, il n'y en a qu'un monté à la fois, et
 * il ne se reflète pas dans le DOM du document. Un `useState` suffit — la
 * lecture du stockage est **paresseuse**, donc faite au montage seulement.
 */
function useSidebarCollapsed() {
  const [isCollapsed, setCollapsed] = useState(
    () => readStorage(STORAGE_KEY) === "true",
  );

  const toggle = () => {
    setCollapsed((collapsed) => {
      writeStorage(STORAGE_KEY, String(!collapsed));
      return !collapsed;
    });
  };

  return { isCollapsed, toggle };
}

/**
 * Barre latérale de bureau.
 *
 * Absente sous `lg` : en dessous, la navigation passe par le tiroir de
 * l'en-tête (cf. `AppHeader`), qui rend exactement les mêmes composants.
 */
export function AppSidebar() {
  const { isCollapsed, toggle } = useSidebarCollapsed();
  const tg = useTranslations("global");
  const ta = useTranslations("a11y");

  return (
    <aside
      className={`relative hidden shrink-0 flex-col border-r border-separator bg-surface transition-[width] duration-200 ease-out motion-reduce:transition-none lg:flex ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      {/*
        Zone de marque, et **rien d'autre** : `h-16` et le filet bas la calent
        exactement sur l'en-tête d'à côté, si bien que la ligne des 64 px
        traverse toute l'application d'un bord à l'autre.
      */}
      <div
        className={`flex h-16 shrink-0 items-center gap-2 border-b border-separator px-4 ${
          isCollapsed ? "justify-center" : ""
        }`}
      >
        {/*
          La marque ramène à la racine — la convention de tout site, et le seul
          moyen de revenir au tableau de bord une fois replié, où le lien
          « Tableau de bord » n'a plus de texte.

          `aria-label` explicite : replié, le lien n'a plus que le logo, qui est
          décoratif ; sans lui, il s'annoncerait par son URL.
        */}
        <Link
          aria-label={isCollapsed ? ta("home") : undefined}
          className="flex min-w-0 items-center gap-2 rounded-lg px-1 py-1 hover:bg-surface-hover hover:no-underline"
          href={ROUTES.dashboard}
        >
          <AppLogo className="size-8 shrink-0 rounded-lg" />

          {isCollapsed ? null : (
            <span className="truncate font-semibold tracking-tight">
              {tg("app-name")}
            </span>
          )}
        </Link>
      </div>

      {/*
        La poignée de repli, à cheval sur la bordure, à l'intersection des deux
        filets. Deux raisons de la sortir du flux plutôt que de la remettre en
        haut de la barre : sa position ne dépend plus de l'état — replié, elle
        ne s'empile pas sous le logo faute de place —, et elle cesse de se lire
        comme un septième lien de navigation.

        ⚠️ Le fond ne se pose pas avec `bg-*` : en variante `outline` le bouton
        est **transparent**, et à cheval sur un filet la ligne lui traverserait
        le ventre. On écrit `--button-bg`, la variable que les variantes de
        HeroUI renseignent.
      */}
      <Button
        aria-label={ta(isCollapsed ? "expand-sidebar" : "collapse-sidebar")}
        className="absolute top-16 right-0 z-30 translate-x-1/2 -translate-y-1/2 [--button-bg-hover:var(--default)] [--button-bg-pressed:var(--default)] [--button-bg:var(--surface)]"
        isIconOnly
        size="sm"
        variant="outline"
        onPress={toggle}
      >
        <ChevronLeftIcon
          className={`transition-transform ${isCollapsed ? "rotate-180" : ""}`}
        />
      </Button>

      {/* La liste défile seule : le compte reste visible quel que soit le
          nombre d'écrans. */}
      {/* `px-3` : dans un rail de 64 px il reste 40 px utiles, la largeur
          exacte des carrés d'icônes. */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <SidebarNav isCollapsed={isCollapsed} />
      </div>

      <div className="border-t border-separator p-3">
        <AccountCard isCollapsed={isCollapsed} />
      </div>
    </aside>
  );
}
